import { useEffect, useState } from "react";
import { Heart, Bell } from "lucide-react";
import { toast } from "sonner";
import { api, errText, formatDate } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { ItemCard } from "../components/ItemCard";
import { Switch } from "../components/ui/switch";

export function Favorites() {
  const [items, setItems] = useState(null);
  useEffect(() => {
    api.get("/favorites").then(({ data }) => setItems(data.map((i) => ({ ...i, is_favorite: true })))).catch(() => setItems([]));
  }, []);
  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
      <h1 className="text-2xl sm:text-4xl font-bold mb-6 flex items-center gap-3"><Heart className="w-7 h-7 sm:w-8 sm:h-8 text-accent" /> Favoriten</h1>
      {items === null ? (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">{[...Array(4)].map((_, i) => <div key={i} className="aspect-[4/5] bg-muted rounded-2xl animate-pulse" />)}</div>
      ) : items.length === 0 ? (
        <p className="text-muted-foreground text-sm py-10 text-center" data-testid="favorites-empty">Noch keine Favoriten. Tippe auf das Herz bei Artikeln, die dir gefallen.</p>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 sm:gap-6">
          {items.map((it, i) => <ItemCard key={it.id} item={it} index={i} />)}
        </div>
      )}
    </div>
  );
}

export function Notifications() {
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get("/notifications").then(({ data }) => {
      setData(data.notifications);
      if (data.unread > 0) api.post("/notifications/read").catch(() => {});
    }).catch(() => setData([]));
  }, []);
  return (
    <div className="max-w-2xl mx-auto px-4 sm:px-6 py-8">
      <h1 className="text-2xl sm:text-4xl font-bold mb-6 flex items-center gap-3"><Bell className="w-7 h-7 sm:w-8 sm:h-8" /> Benachrichtigungen</h1>
      <div className="space-y-2.5" data-testid="notification-list">
        {data === null && [...Array(3)].map((_, i) => <div key={i} className="h-16 bg-muted rounded-2xl animate-pulse" />)}
        {data?.length === 0 && <p className="text-muted-foreground text-sm py-10 text-center">Keine Benachrichtigungen.</p>}
        {data?.map((n) => (
          <div key={n.id} className={`border rounded-2xl p-4 ${n.read ? "border-border" : "border-primary/40 bg-secondary/40"}`}>
            <p className="font-semibold text-sm">{n.title}</p>
            <p className="text-sm text-muted-foreground mt-0.5">{n.body}</p>
            <p className="text-xs text-muted-foreground mt-1">{formatDate(n.created_at)}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

export function Account() {
  const { user, refresh } = useAuth();
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (user) setForm({ name: user.name || "", username: user.username || "", bio: user.bio || "",
      phone: user.phone || "", notification_settings: user.notification_settings || {},
      address: user.address || { street: "", zip: "", city: "", country: "Deutschland" } });
  }, [user]);

  if (!form) return null;
  const input = "w-full rounded-xl border border-border bg-card px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-ring";

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.put("/auth/profile", form);
      await refresh();
      toast.success("Profil gespeichert");
    } catch (err) { toast.error(errText(err)); } finally { setBusy(false); }
  };

  const notifLabels = { new_message: "Neue Nachrichten", item_sold: "Artikel verkauft", payment: "Zahlungen", shipping: "Versand & Zustellung", payout: "Auszahlungen", review: "Bewertungen" };

  return (
    <div className="max-w-xl mx-auto px-4 sm:px-6 py-8">
      <h1 className="text-4xl font-bold mb-6">Mein Konto</h1>
      <p className="text-sm text-muted-foreground mb-6">{user.email} {user.email_verified ? "· verifiziert" : "· E-Mail nicht verifiziert"}</p>
      <form onSubmit={save} className="space-y-4">
        <input data-testid="account-name-input" className={input} placeholder="Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
        <input data-testid="account-username-input" className={input} placeholder="Benutzername" value={form.username} onChange={(e) => setForm({ ...form, username: e.target.value })} />
        <textarea data-testid="account-bio-input" className={`${input} min-h-20`} placeholder="Über mich" value={form.bio} onChange={(e) => setForm({ ...form, bio: e.target.value })} />
        <input data-testid="account-phone-input" className={input} placeholder="Telefon (optional)" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
        <h2 className="text-base font-bold pt-2">Adresse</h2>
        <input data-testid="account-street-input" className={input} placeholder="Straße" value={form.address.street || ""} onChange={(e) => setForm({ ...form, address: { ...form.address, street: e.target.value } })} />
        <div className="grid grid-cols-3 gap-3">
          <input data-testid="account-zip-input" className={input} placeholder="PLZ" value={form.address.zip || ""} onChange={(e) => setForm({ ...form, address: { ...form.address, zip: e.target.value } })} />
          <input data-testid="account-city-input" className={`${input} col-span-2`} placeholder="Stadt" value={form.address.city || ""} onChange={(e) => setForm({ ...form, address: { ...form.address, city: e.target.value } })} />
        </div>
        <h2 className="text-base font-bold pt-2">Benachrichtigungen</h2>
        <div className="space-y-3 border border-border rounded-2xl p-4">
          {Object.entries(notifLabels).map(([k, label]) => (
            <div key={k} className="flex items-center justify-between">
              <span className="text-sm">{label}</span>
              <Switch data-testid={`notif-switch-${k}`} checked={form.notification_settings[k] !== false}
                onCheckedChange={(v) => setForm({ ...form, notification_settings: { ...form.notification_settings, [k]: v } })} />
            </div>
          ))}
        </div>
        <button data-testid="account-save-btn" disabled={busy} className="w-full bg-primary text-primary-foreground font-bold py-3.5 rounded-full hover:opacity-90 disabled:opacity-50">Speichern</button>
      </form>
    </div>
  );
}
